import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  ArrowRight,
  ArrowsLeftRight,
  ChartLineUp,
  Compass,
  FileText,
  GearSix,
  House,
  LockKey,
  MapTrifold,
  ShieldCheck,
  SignIn,
  Sparkle,
  UploadSimple,
} from "@phosphor-icons/react";
import {
  LOCALES,
  detectLocale,
  messages,
  type Locale,
  type Preferences,
} from "@sporttrack/contracts";
import { PrivateWorkspace } from "./PrivateWorkspace";
import { cooperativelyVisit } from "./cooperative-render";

type Sport = "cycling" | "running" | "hiking";

interface DemoMetric {
  key: string;
  value: number | null;
  unit: string;
  state: string;
  provenance: string;
  coverage: number;
}

interface DemoActivity {
  id: string;
  sport: Sport;
  title: string;
  metrics: DemoMetric[];
  limitation: string;
}

interface DemoData {
  fictional: true;
  provenance: string;
  copy: { promise: string; fit: string; kml: string; privacy: string; boundary: string };
  activities: DemoActivity[];
}

interface Envelope<T> {
  data: T;
  warnings: string[];
}

const bilingualCopy = {
  en: {
    skip: "Skip to content",
    demo: "Explore fictional demo",
    eligibility: "Create account",
    signIn: "Sign in",
    nav: ["Home", "Activities", "Compare", "Reports", "Studio", "Settings"],
    heroEyebrow: "Private evidence for imperfect activity files",
    heroTitle: "Your route tells a story. Keep it yours.",
    heroBody:
      "SportTrack turns FIT and KML files into sport-aware evidence, with provenance, coverage, and limitations attached.",
    fictional: "Fictional data · no account needed",
    private: "Private by default",
    precise: "Evidence before claims",
    portable: "Portable and deletable",
    proofTitle: "A calmer way to understand a hard day out",
    proofBody:
      "Missing sensors stay missing. Coasting stays zero. Geometry-only routes never pretend to know your pace.",
    fit: "FIT keeps time, laps, device context, and supported sensor observations.",
    kml: "KML keeps declared route segments and elevation where present; time may remain unavailable.",
    boundary:
      "SportTrack describes recorded observations. It is not medical advice and does not infer readiness, recovery, or injury risk.",
    eligibilityTitle: "Private activity analysis, available worldwide",
    eligibilityBody:
      "Create a private account from anywhere. No route or sensor file is collected here.",
    check: "Continue securely",
    signInEyebrow: "Private account",
    joinEyebrow: "Verified enrollment",
    studioEyebrow: "Fixed snapshot / local canvas",
    studioTemplate: "4:5 / ORIGINAL TEMPLATE",
    privacyChip: "PRIVATE / CORE ONLY",
    avatarLabel: "Demo athlete",
    demoEyebrow: "Fictional activity evidence",
    demoLoading: "Loading fictional activities",
    routeProgress: "Route progress",
    demoTitle: "Three fictional outings. Three different evidence boundaries.",
    demoIntro:
      "Every value below belongs to the synthetic SportTrack corpus. It represents no person, device, home, or routine.",
    how: "How this was calculated",
    coverage: "coverage",
    unavailable: "Unavailable",
    derived: "Calculated",
    recorded: "Recorded",
    routeSummary: "Synthetic route shape and elevation summary",
    pause: "Pause",
    play: "Play",
    reset: "Reset",
    appGreeting: "Your evidence, at a glance",
    appEmpty: "Import a synthetic FIT or KML fixture to start your private history.",
    import: "Import activity",
    jobs: "Jobs and recovery",
    noJobs: "No work needs attention.",
    observed: "Observed differences only",
    reports: "Immutable reports",
    studio: "Privacy-safe Studio",
    settings: "Privacy and account controls",
    maplessFirst: "Mapless sharing is always offered first.",
    png: "Create local PNG",
    pngHelp:
      "The canvas is generated locally from the reviewed fixed snapshot and contains no EXIF metadata.",
    locale: "中文",
  },
  "zh-CN": {
    skip: "跳到主要内容",
    demo: "体验示例演示",
    eligibility: "创建账户",
    signIn: "登录",
    nav: ["首页", "活动", "比较", "报告", "工作室", "设置"],
    heroEyebrow: "不完美的活动文件，也能变成可靠的私密证据",
    heroTitle: "路线讲述你的故事，而故事始终由你做主。",
    heroBody: "SportTrack 把 FIT 和 KML 文件转化为贴合具体运动的证据，并标注来源、覆盖范围与限制。",
    fictional: "示例数据 · 无需账户",
    private: "默认私密",
    precise: "证据先行",
    portable: "可导出、可删除",
    proofTitle: "更从容地读懂一次艰难的户外活动",
    proofBody:
      "缺失的传感器不会被凭空补齐；滑行时功率就是零；只有几何数据的路线，不会假装知道你的配速。",
    fit: "FIT 可保留时间、圈数、设备信息以及支持的传感器数据。",
    kml: "KML 可保留路线分段和已有的海拔数据；时间信息可能仍然缺失。",
    boundary:
      "SportTrack 只描述已记录的观测结果，不提供医疗建议，也不推断训练准备状态、恢复情况或受伤风险。",
    eligibilityTitle: "私密活动分析，全球可用",
    eligibilityBody: "无论身在何处，都能创建私密账户；这里不会收集任何路线或传感器文件。",
    check: "放心继续",
    signInEyebrow: "私密账户",
    joinEyebrow: "已验证注册",
    studioEyebrow: "固定快照 / 本地画布",
    studioTemplate: "4:5 / 原始模板",
    privacyChip: "私密 · 仅核心数据",
    avatarLabel: "示例运动员",
    demoEyebrow: "示例活动证据",
    demoLoading: "正在加载示例活动…",
    routeProgress: "路线进度",
    demoTitle: "三个示例活动，三种不同的数据完整度。",
    demoIntro:
      "以下所有数值均来自 SportTrack 的合成数据，不代表任何真实的个人、设备、家庭或日常作息。",
    how: "计算方式",
    coverage: "覆盖率",
    unavailable: "不可用",
    derived: "已计算",
    recorded: "已记录",
    routeSummary: "合成路线的走向与海拔概览",
    pause: "暂停",
    play: "播放",
    reset: "重置",
    appGreeting: "你的证据，一目了然",
    appEmpty: "导入合成的 FIT 或 KML 示例，开始积累你的私密记录。",
    import: "导入活动",
    jobs: "任务与恢复",
    noJobs: "目前没有需要处理的任务。",
    observed: "只说明观察到的差异",
    reports: "不可变报告",
    studio: "隐私安全工作室",
    settings: "隐私与账户控制",
    maplessFirst: "分享时始终优先提供无地图模式。",
    png: "创建本地 PNG",
    pngHelp: "图像只根据已审核的固定快照在本地生成，不包含 EXIF 元数据。",
    locale: "EN",
  },
} as const;

type AppCopy = {
  [K in keyof typeof bilingualCopy.en]: (typeof bilingualCopy.en)[K] extends readonly string[]
    ? readonly string[]
    : string;
};

const translatedCopy = (overrides: Partial<AppCopy>): AppCopy => ({
  ...bilingualCopy.en,
  ...overrides,
});

const copy: Record<Locale, AppCopy> = {
  en: bilingualCopy.en,
  "zh-CN": bilingualCopy["zh-CN"],
  fr: translatedCopy({
    skip: "Aller au contenu",
    demo: "Explorer la démo fictive",
    eligibility: "Vérifier l’éligibilité",
    signIn: "Se connecter",
    nav: ["Accueil", "Activités", "Comparer", "Rapports", "Studio", "Paramètres"],
    heroEyebrow: "Des preuves privées pour des fichiers d’activité imparfaits",
    heroTitle: "Votre parcours raconte une histoire. Gardez-la privée.",
    heroBody:
      "SportTrack transforme les fichiers FIT et KML en preuves adaptées au sport, avec origine, couverture et limites.",
    fictional: "Données fictives · aucun compte requis",
    private: "Privé par défaut",
    precise: "Les preuves avant les affirmations",
    portable: "Portable et supprimable",
    proofTitle: "Une manière plus sereine de comprendre une sortie difficile",
    proofBody:
      "Les capteurs absents restent absents. La roue libre reste à zéro. Une trace géométrique n’invente jamais votre allure.",
    fit: "FIT conserve le temps, les tours, l’appareil et les capteurs pris en charge.",
    kml: "KML conserve les segments déclarés et l’altitude disponible ; le temps peut rester indisponible.",
    boundary:
      "SportTrack décrit les observations enregistrées. Il ne fournit aucun conseil médical et ne déduit ni préparation, ni récupération, ni risque de blessure.",
    eligibilityTitle: "Analyse privée des activités, disponible partout",
    eligibilityBody:
      "Créez un compte privé où que vous soyez. Aucun parcours ni fichier de capteur n’est collecté ici.",
    check: "Continuer en toute sécurité",
    signInEyebrow: "Compte privé",
    joinEyebrow: "Inscription vérifiée",
    studioEyebrow: "Instantané fixe / canevas local",
    studioTemplate: "4:5 / MODÈLE ORIGINAL",
    privacyChip: "PRIVÉ / DONNÉES CŒUR UNIQUEMENT",
    avatarLabel: "Athlète de démonstration",
    demoEyebrow: "Preuves d’activité fictives",
    demoLoading: "Chargement des activités fictives…",
    routeProgress: "Progression du parcours",
    demoTitle: "Trois sorties fictives. Trois limites de preuve différentes.",
    demoIntro:
      "Toutes les valeurs proviennent du corpus synthétique SportTrack et ne représentent aucune personne ni routine.",
    coverage: "couverture",
    unavailable: "Indisponible",
    derived: "Calculé",
    recorded: "Enregistré",
    pause: "Pause",
    play: "Lire",
    reset: "Réinitialiser",
    how: "Méthode de calcul",
    routeSummary: "Forme du parcours synthétique et résumé d’altitude",
    appGreeting: "Vos preuves en un coup d’œil",
    appEmpty: "Importez un exemple FIT ou KML synthétique pour commencer votre historique privé.",
    import: "Importer une activité",
    jobs: "Tâches et récupération",
    noJobs: "Aucune tâche ne requiert votre attention.",
    observed: "Différences observées uniquement",
    reports: "Rapports immuables",
    studio: "Studio respectueux de la vie privée",
    settings: "Confidentialité et compte",
    maplessFirst: "Le partage sans carte est toujours proposé en premier.",
    png: "Créer un PNG local",
    pngHelp:
      "L’image est créée localement depuis l’instantané fixe vérifié et ne contient aucune donnée EXIF.",
  }),
  de: translatedCopy({
    skip: "Zum Inhalt springen",
    demo: "Fiktive Demo ansehen",
    eligibility: "Konto erstellen",
    signIn: "Anmelden",
    nav: ["Start", "Aktivitäten", "Vergleichen", "Berichte", "Studio", "Einstellungen"],
    heroEyebrow: "Private Nachweise für unvollständige Aktivitätsdateien",
    heroTitle: "Deine Route erzählt eine Geschichte. Behalte sie für dich.",
    heroBody:
      "SportTrack verwandelt FIT- und KML-Dateien in sportbezogene Nachweise mit Herkunft, Abdeckung und Einschränkungen.",
    fictional: "Fiktive Daten · kein Konto nötig",
    private: "Standardmäßig privat",
    precise: "Nachweise vor Aussagen",
    portable: "Exportierbar und löschbar",
    proofTitle: "Ein ruhigerer Blick auf einen anstrengenden Tag",
    proofBody:
      "Fehlende Sensoren bleiben fehlend. Rollen bleibt null. Reine Routengeometrie erfindet kein Tempo.",
    fit: "FIT bewahrt Zeit, Runden, Gerätekontext und unterstützte Sensorwerte.",
    kml: "KML bewahrt angegebene Routensegmente und vorhandene Höhe; Zeit kann unverfügbar bleiben.",
    boundary:
      "SportTrack beschreibt aufgezeichnete Beobachtungen. Es gibt keine medizinischen Ratschläge und leitet weder Bereitschaft, Erholung noch Verletzungsrisiko ab.",
    eligibilityTitle: "Private Aktivitätsanalyse, weltweit verfügbar",
    eligibilityBody:
      "Erstelle überall ein privates Konto. Routen- und Sensordateien werden hier nicht erhoben.",
    check: "Sicher fortfahren",
    coverage: "Abdeckung",
    unavailable: "Nicht verfügbar",
    derived: "Berechnet",
    recorded: "Aufgezeichnet",
    pause: "Pause",
    play: "Abspielen",
    reset: "Zurücksetzen",
    demoTitle: "Drei fiktive Ausflüge. Drei unterschiedliche Nachweisgrenzen.",
    demoEyebrow: "Fiktive Aktivitätsnachweise",
    signInEyebrow: "Privates Konto",
    joinEyebrow: "Verifizierte Registrierung",
    studioEyebrow: "Fixierter Schnappschuss / lokale Leinwand",
    studioTemplate: "4:5 / ORIGINALVORLAGE",
    privacyChip: "PRIVAT / NUR KERNDATEN",
    avatarLabel: "Demo-Athlet",
    demoLoading: "Fiktive Aktivitäten werden geladen…",
    routeProgress: "Routenfortschritt",
    demoIntro:
      "Alle Werte stammen aus dem synthetischen SportTrack-Korpus und repräsentieren keine Person, kein Gerät und keine Routine.",
    how: "Berechnungsmethode",
    routeSummary: "Synthetische Routenform und Höhenübersicht",
    appGreeting: "Deine Nachweise auf einen Blick",
    appEmpty:
      "Importiere ein synthetisches FIT- oder KML-Beispiel, um deinen privaten Verlauf zu beginnen.",
    import: "Aktivität importieren",
    jobs: "Aufgaben und Wiederherstellung",
    noJobs: "Keine Aufgabe erfordert Aufmerksamkeit.",
    observed: "Nur beobachtete Unterschiede",
    reports: "Unveränderliche Berichte",
    studio: "Datenschutzfreundliches Studio",
    settings: "Datenschutz und Konto",
    maplessFirst: "Die Freigabe ohne Karte wird immer zuerst angeboten.",
    png: "Lokales PNG erstellen",
    pngHelp:
      "Das Bild wird lokal aus dem geprüften fixierten Schnappschuss erstellt und enthält keine EXIF-Daten.",
  }),
  it: translatedCopy({
    skip: "Vai al contenuto",
    demo: "Esplora la demo fittizia",
    eligibility: "Verifica l’idoneità",
    eligibilityTitle: "Analisi privata delle attività, disponibile ovunque",
    eligibilityBody:
      "Crea un account privato ovunque ti trovi. Qui non vengono raccolti percorsi o file dei sensori.",
    signIn: "Accedi",
    nav: ["Home", "Attività", "Confronta", "Rapporti", "Studio", "Impostazioni"],
    heroTitle: "Il tuo percorso racconta una storia. Tienila privata.",
    fictional: "Dati fittizi · nessun account richiesto",
    private: "Privato per impostazione predefinita",
    demoEyebrow: "Prove di attività fittizie",
    signInEyebrow: "Account privato",
    joinEyebrow: "Registrazione verificata",
    studioEyebrow: "Istantanea fissa / tela locale",
    studioTemplate: "4:5 / MODELLO ORIGINALE",
    privacyChip: "PRIVATO / SOLO DATI ESSENZIALI",
    avatarLabel: "Atleta dimostrativo",
    demoLoading: "Caricamento delle attività fittizie…",
    routeProgress: "Avanzamento del percorso",
    coverage: "copertura",
    unavailable: "Non disponibile",
    derived: "Calcolato",
    recorded: "Registrato",
    pause: "Pausa",
    play: "Riproduci",
    reset: "Reimposta",
  }),
  nl: translatedCopy({
    skip: "Naar inhoud",
    demo: "Fictieve demo bekijken",
    eligibility: "Account maken",
    eligibilityTitle: "Privéanalyse van activiteiten, wereldwijd beschikbaar",
    eligibilityBody:
      "Maak overal een privéaccount. Hier worden geen route- of sensorbestanden verzameld.",
    signIn: "Inloggen",
    nav: ["Home", "Activiteiten", "Vergelijken", "Rapporten", "Studio", "Instellingen"],
    heroTitle: "Je route vertelt een verhaal. Houd het van jou.",
    fictional: "Fictieve gegevens · geen account nodig",
    private: "Standaard privé",
    demoEyebrow: "Fictieve activiteitsbewijzen",
    signInEyebrow: "Privéaccount",
    joinEyebrow: "Geverifieerde inschrijving",
    studioEyebrow: "Vaste momentopname / lokaal canvas",
    studioTemplate: "4:5 / ORIGINEEL SJABLOON",
    privacyChip: "PRIVÉ / ALLEEN KERNGEGEVENS",
    avatarLabel: "Demo-atleet",
    demoLoading: "Fictieve activiteiten laden…",
    routeProgress: "Routevoortgang",
    coverage: "dekking",
    unavailable: "Niet beschikbaar",
    derived: "Berekend",
    recorded: "Geregistreerd",
    pause: "Pauze",
    play: "Afspelen",
    reset: "Resetten",
  }),
  es: translatedCopy({
    skip: "Ir al contenido",
    demo: "Explorar la demo ficticia",
    eligibility: "Crear una cuenta",
    eligibilityTitle: "Análisis privado de actividades, disponible en todo el mundo",
    eligibilityBody:
      "Crea una cuenta privada estés donde estés. Aquí no se recopilan rutas ni archivos de sensores.",
    signIn: "Iniciar sesión",
    nav: ["Inicio", "Actividades", "Comparar", "Informes", "Studio", "Ajustes"],
    heroEyebrow: "Datos privados para archivos de actividad imperfectos",
    heroTitle: "Tu ruta cuenta una historia. Haz que siga siendo tuya.",
    heroBody:
      "SportTrack convierte archivos FIT y KML en datos adaptados al deporte, con procedencia, cobertura y limitaciones.",
    fictional: "Datos ficticios · sin cuenta",
    private: "Privado de forma predeterminada",
    demoEyebrow: "Pruebas de actividad ficticias",
    signInEyebrow: "Cuenta privada",
    joinEyebrow: "Inscripción verificada",
    studioEyebrow: "Instantánea fija / lienzo local",
    studioTemplate: "4:5 / PLANTILLA ORIGINAL",
    privacyChip: "PRIVADO / SOLO DATOS ESENCIALES",
    avatarLabel: "Atleta de demostración",
    demoLoading: "Cargando actividades ficticias…",
    routeProgress: "Progreso de la ruta",
    precise: "Los datos antes que las afirmaciones",
    portable: "Portable y eliminable",
    coverage: "cobertura",
    unavailable: "No disponible",
    derived: "Calculado",
    recorded: "Registrado",
    pause: "Pausa",
    play: "Reproducir",
    reset: "Restablecer",
  }),
};

function useLocale(): [Locale, (next: Locale) => void] {
  const [locale, setLocale] = useState<Locale>(() =>
    detectLocale(localStorage.getItem("sporttrack-locale"), navigator.languages),
  );
  const changeLocale = useCallback((next: Locale) => {
    localStorage.setItem("sporttrack-locale", next);
    setLocale(next);
  }, []);
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
  return [locale, changeLocale];
}

function LanguageSelect({
  locale,
  onChange,
}: {
  locale: Locale;
  onChange: (next: Locale) => void;
}) {
  return (
    <label className="language-select">
      <span className="sr-only">{messages[locale].language}</span>
      <select
        aria-label={messages[locale].language}
        value={locale}
        onChange={(event) => onChange(event.target.value as Locale)}
      >
        {LOCALES.map((item) => (
          <option key={item.code} value={item.code}>
            {item.nativeName}
          </option>
        ))}
      </select>
    </label>
  );
}

function navigate(path: string): void {
  history.pushState({}, "", path);
  dispatchEvent(new PopStateEvent("popstate"));
  window.scrollTo({
    top: 0,
    behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
  });
}

function Link({
  href,
  children,
  className = "",
}: {
  href: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <a
      className={className}
      href={href}
      onClick={(event) => {
        if (event.button === 0 && !event.metaKey && !event.ctrlKey) {
          event.preventDefault();
          navigate(href);
        }
      }}
    >
      {children}
    </a>
  );
}

function Mark() {
  return (
    <span className="mark" aria-hidden="true">
      <i />
      <i />
      <i />
    </span>
  );
}

function PublicHeader({ locale, toggle }: { locale: Locale; toggle: (next: Locale) => void }) {
  const t = copy[locale];
  return (
    <header className="public-header">
      <Link href="/" className="brand">
        <Mark />
        <span>SportTrack</span>
      </Link>
      <nav aria-label={messages[locale].publicNavigation}>
        <Link href="/demo">{t.demo}</Link>
        <a href="#eligibility">{t.eligibility}</a>
        <LanguageSelect locale={locale} onChange={toggle} />
        <Link href="/app" className="button small secondary">
          <SignIn aria-hidden="true" size={16} weight="bold" />
          {t.signIn}
        </Link>
      </nav>
    </header>
  );
}

function Landing({ locale, toggle }: { locale: Locale; toggle: (next: Locale) => void }) {
  const t = copy[locale];
  const publicDemoOnly = import.meta.env.VITE_LAUNCH_PHASE === "public-demo";
  return (
    <div className="public-page">
      <PublicHeader locale={locale} toggle={toggle} />
      <main id="main">
        <section className="hero shell">
          <div className="hero-copy">
            <p className="eyebrow">{t.heroEyebrow}</p>
            <h1>{t.heroTitle}</h1>
            <p className="lede">{t.heroBody}</p>
            <div className="hero-actions">
              <Link href="/demo" className="button">
                {t.demo}
                <ArrowRight aria-hidden="true" size={18} weight="bold" />
              </Link>
              <a className="quiet-link" href="#principles">
                {t.how}
              </a>
            </div>
          </div>
          <HeroVisual locale={locale} />
        </section>

        <section className="trust-strip shell" aria-label={t.fictional}>
          <p>{t.fictional}</p>
          <div>
            <span>{t.private}</span>
            <span>{t.precise}</span>
            <span>{t.portable}</span>
          </div>
        </section>

        <section id="principles" className="principles shell" aria-labelledby="principles-title">
          <div>
            <h2 id="principles-title">{t.proofTitle}</h2>
            <p>{t.proofBody}</p>
          </div>
          <div className="principle-grid">
            <article>
              <LockKey aria-hidden="true" size={24} weight="regular" />
              <h3>{t.private}</h3>
              <p>
                {locale !== "zh-CN"
                  ? "Original routes and source files stay in private storage."
                  : "原始路线和源文件只会保存在私密存储中。"}
              </p>
            </article>
            <article>
              <ChartLineUp aria-hidden="true" size={24} weight="regular" />
              <h3>{t.precise}</h3>
              <p>
                {locale !== "zh-CN"
                  ? "Each metric carries state, provenance, coverage, and limits."
                  : "每项指标都带有状态、来源、覆盖范围和限制。"}
              </p>
            </article>
            <article>
              <UploadSimple aria-hidden="true" size={24} weight="regular" />
              <h3>{t.portable}</h3>
              <p>
                {locale !== "zh-CN"
                  ? "Owner-only exports and tracked deletion are core controls."
                  : "导出仅限本人，删除也有迹可循，这些是核心控制。"}
              </p>
            </article>
          </div>
        </section>

        <section className="formats shell">
          <article className="format-card fit">
            <img
              src="/sporttrack-fit-cyclist.jpg"
              alt={
                locale !== "zh-CN"
                  ? "Anonymous cyclist riding on a quiet mountain road"
                  : "一位匿名骑行者骑行在宁静的山间公路上"
              }
              loading="lazy"
              decoding="async"
              width="1448"
              height="1086"
            />
            <div>
              <p className="format-label">.FIT</p>
              <h2>
                {locale !== "zh-CN"
                  ? "Time and sensors, honestly qualified"
                  : "时间与传感器数据，如实呈现"}
              </h2>
              <p>{t.fit}</p>
            </div>
          </article>
          <article className="format-card kml">
            <img
              src="/sporttrack-route-terrain.jpg"
              alt={
                locale !== "zh-CN"
                  ? "A winding path across a green hillside"
                  : "一条蜿蜒穿过绿色山坡的小径"
              }
              loading="lazy"
              decoding="async"
              width="1448"
              height="1086"
            />
            <div>
              <p className="format-label">.KML</p>
              <h2>
                {locale !== "zh-CN" ? "A route without invented pace" : "只有路线，绝不编造配速"}
              </h2>
              <p>{t.kml}</p>
            </div>
          </article>
        </section>

        <section className="boundary shell">
          <ShieldCheck aria-hidden="true" size={28} weight="regular" />
          <p>{t.boundary}</p>
        </section>
        {publicDemoOnly ? (
          <section className="eligibility shell" aria-labelledby="eligibility-title">
            <div>
              <h2 id="eligibility-title">
                {locale !== "zh-CN"
                  ? "Account creation is unavailable here"
                  : "此处暂不支持创建账户"}
              </h2>
              <p>
                {locale !== "zh-CN"
                  ? "Explore the fictional demo without creating an account or uploading personal activity data."
                  : "无需创建账户或上传个人活动数据，即可体验示例演示。"}
              </p>
            </div>
            <Link className="button" href="/demo">
              {t.demo}
            </Link>
          </section>
        ) : (
          <Eligibility locale={locale} />
        )}
      </main>
      <footer className="public-footer shell">
        <span>SportTrack</span>
        <a href="/THIRD-PARTY-NOTICES.txt">
          {locale !== "zh-CN" ? "Third-party notices" : "第三方许可声明"}
        </a>
        <span>
          {locale !== "zh-CN" ? "No ads. No behavioral tracking." : "无广告，不做行为追踪。"}
        </span>
      </footer>
    </div>
  );
}

function HeroVisual({ locale }: { locale: Locale }) {
  return (
    <figure className="hero-visual">
      <img
        src="/sporttrack-ridge-runner.jpg"
        alt={
          locale !== "zh-CN"
            ? "Anonymous trail runner following a misty mountain path"
            : "一位匿名越野跑者沿雾气弥漫的山路前行"
        }
        width="1448"
        height="1086"
        fetchPriority="high"
        decoding="async"
      />
      <figcaption>
        <MapTrifold aria-hidden="true" size={18} weight="regular" />
        {locale !== "zh-CN"
          ? "Your source route stays private until you choose otherwise."
          : "除非你主动选择分享，否则源路线始终保持私密。"}
      </figcaption>
    </figure>
  );
}

function Eligibility({ locale }: { locale: Locale }) {
  const t = copy[locale];
  const [state, setState] = useState<"idle" | "loading" | "eligible" | "stopped" | "error">("idle");
  async function submit(event: FormEvent) {
    event.preventDefault();
    setState("loading");
    try {
      const response = await fetch("/v1/eligibility", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          policyVersion: "eligibility-v1",
        }),
      });
      const result = (await response.json()) as Envelope<{
        eligible: boolean;
        evidenceId: string | null;
      }>;
      if (response.ok && result.data?.eligible && result.data.evidenceId !== null) {
        sessionStorage.setItem("sporttrack-evidence", result.data.evidenceId);
        setState("eligible");
      } else setState(response.ok ? "stopped" : "error");
    } catch {
      setState("error");
    }
  }
  return (
    <section id="eligibility" className="eligibility shell" aria-labelledby="eligibility-title">
      <div>
        <h2 id="eligibility-title">{t.eligibilityTitle}</h2>
        <p>{t.eligibilityBody}</p>
      </div>
      <form onSubmit={(event) => void submit(event)} aria-busy={state === "loading"}>
        <button className="button" disabled={state === "loading"}>
          {state === "loading" ? "…" : t.check}
        </button>
        <p className="form-status" role="status">
          {state === "eligible"
            ? locale !== "zh-CN"
              ? "Eligible. Continue to secure account creation."
              : "资格检查通过，可以安全创建账户。"
            : state === "stopped"
              ? locale !== "zh-CN"
                ? "Account creation is unavailable, but the fictional demo remains open."
                : "目前暂不支持创建账户，但示例演示仍可使用。"
              : state === "error"
                ? locale !== "zh-CN"
                  ? "Eligibility could not be checked. Please try again."
                  : "资格检查未完成，请重试。"
                : ""}
        </p>
        {state === "eligible" && (
          <Link className="button secondary" href="/join">
            {locale !== "zh-CN" ? "Create verified account" : "创建并验证账户"}
          </Link>
        )}
      </form>
    </section>
  );
}

function Demo({ locale, toggle }: { locale: Locale; toggle: (next: Locale) => void }) {
  const t = copy[locale];
  const [data, setData] = useState<DemoData | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    fetch(`/v1/public/demo?locale=${locale}`, { signal: controller.signal })
      .then((response) => response.json())
      .then((payload: Envelope<DemoData>) => setData(payload.data))
      .catch((reason: unknown) => {
        if (!(reason instanceof DOMException)) setError(true);
      });
    return () => controller.abort();
  }, [locale]);
  return (
    <div className="public-page demo-page">
      <PublicHeader locale={locale} toggle={toggle} />
      <main id="main" className="shell demo-main">
        <header>
          <p className="eyebrow">{t.demoEyebrow}</p>
          <h1>{t.demoTitle}</h1>
          <p className="lede">{t.demoIntro}</p>
        </header>
        {error ? (
          <div className="notice error" role="alert">
            {locale !== "zh-CN"
              ? "The fictional demo could not load. Retry without uploading a file."
              : "示例演示加载失败，请重试（无需上传文件）。"}
          </div>
        ) : data === null ? (
          <DemoSkeleton label={t.demoLoading} />
        ) : (
          <>
            <p className="provenance">◇ {data.provenance}</p>
            <div className="demo-grid">
              {data.activities.map((activity, index) => (
                <DemoCard key={activity.id} activity={activity} locale={locale} index={index} />
              ))}
            </div>
            <div className="demo-copy-grid">
              <p>{data.copy.fit}</p>
              <p>{data.copy.kml}</p>
              <p>{data.copy.privacy}</p>
              <p>{data.copy.boundary}</p>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function DemoSkeleton({ label }: { label: string }) {
  return (
    <div className="demo-grid" aria-label={label}>
      {[0, 1, 2].map((value) => (
        <div className="demo-card skeleton" key={value} />
      ))}
    </div>
  );
}

function DemoCard({
  activity,
  locale,
  index,
}: {
  activity: DemoActivity;
  locale: Locale;
  index: number;
}) {
  const t = copy[locale];
  const [open, setOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    if (!playing) return;
    const timer = window.setInterval(
      () => setProgress((value) => (value >= 100 ? 0 : value + 1)),
      50,
    );
    return () => clearInterval(timer);
  }, [playing]);
  const metric = (item: DemoMetric) =>
    item.value === null
      ? t.unavailable
      : item.key === "distance"
        ? `${(item.value / 1000).toFixed(1)} km`
        : `${item.value} ${item.unit}`;
  const paths = [
    "M15 85 C30 15 48 91 65 35 S98 60 110 20",
    "M10 70 C25 30 36 84 55 44 S80 10 112 38",
    "M12 83 C24 67 33 18 50 45 S82 89 112 28",
  ];
  return (
    <article className={`demo-card sport-${activity.sport}`}>
      <div className="demo-card-head">
        <span className="sport-glyph">
          {activity.sport === "cycling" ? "◌" : activity.sport === "running" ? "⌁" : "△"}
        </span>
        <span>{activity.sport.toUpperCase()}</span>
        <span>0{index + 1}</span>
      </div>
      <div className="mini-route">
        <svg viewBox="0 0 120 100" role="img" aria-label={t.routeSummary}>
          <path className="ghost-path" d={paths[index]} />
          <path
            className="active-path"
            pathLength="100"
            strokeDasharray={`${progress} 100`}
            d={paths[index]}
          />
        </svg>
        <span className="playhead" style={{ left: `${Math.min(88, 7 + progress * 0.81)}%` }} />
      </div>
      <h2>{activity.title}</h2>
      <dl>
        {activity.metrics.map((item) => (
          <div key={item.key}>
            <dt>{item.key}</dt>
            <dd>{metric(item)}</dd>
            <small>
              {item.state === "recorded"
                ? t.recorded
                : item.state === "derived"
                  ? t.derived
                  : t.unavailable}{" "}
              · {Math.round(item.coverage * 100)}% {t.coverage}
            </small>
          </div>
        ))}
      </dl>
      <p className="limitation">{activity.limitation}</p>
      <div className="playback">
        <button onClick={() => setPlaying((value) => !value)}>{playing ? t.pause : t.play}</button>
        <input
          aria-label={t.routeProgress}
          type="range"
          min="0"
          max="100"
          value={progress}
          onChange={(event) => {
            setPlaying(false);
            setProgress(Number(event.target.value));
          }}
        />
        <button
          onClick={() => {
            setPlaying(false);
            setProgress(0);
          }}
        >
          {t.reset}
        </button>
      </div>
      <button
        className="calculation"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {t.how}
        <span>{open ? "−" : "+"}</span>
      </button>
      {open && (
        <div className="calculation-detail">
          <p>
            {locale !== "zh-CN"
              ? "Eligible samples are evaluated under metric-v1. Coverage and exclusions travel with each value."
              : "符合条件的样本按 metric-v1 规则计算，每个数值都附有覆盖率与排除说明。"}
          </p>
        </div>
      )}
    </article>
  );
}

function AppShell({
  locale,
  toggle,
  section,
  path,
}: {
  locale: Locale;
  toggle: (next: Locale) => void;
  section: string;
  path: string;
}) {
  const t = copy[locale];
  const [accountState, setAccountState] = useState<"loading" | "signed-in" | "signed-out">(
    "loading",
  );
  const [accountPreferences, setAccountPreferences] = useState<
    (Preferences & { version: number }) | null
  >(null);
  useEffect(() => {
    const controller = new AbortController();
    fetch("/v1/account", { credentials: "include", signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) {
          setAccountState("signed-out");
          return;
        }
        const payload = (await response.json()) as Envelope<{
          preferences: (Preferences & { version: number }) | null;
        }>;
        if (payload.data.preferences !== null) {
          setAccountPreferences(payload.data.preferences);
          toggle(payload.data.preferences.locale);
        }
        const theme = payload.data.preferences?.theme ?? "system";
        if (theme === "system") delete document.documentElement.dataset.theme;
        else document.documentElement.dataset.theme = theme;
        setAccountState("signed-in");
      })
      .catch(() => setAccountState("signed-out"));
    return () => controller.abort();
  }, [toggle]);
  if (accountState === "loading")
    return (
      <div className="auth-page">
        <div className="auth-card" aria-busy="true">
          <Mark />
          <p>{locale !== "zh-CN" ? "Checking your private session…" : "正在检查私密会话…"}</p>
        </div>
      </div>
    );
  if (accountState === "signed-out")
    return (
      <AuthGate locale={locale} toggle={toggle} onSignedIn={() => setAccountState("signed-in")} />
    );
  const navItems = [
    { label: "home", Icon: House },
    { label: "activities", Icon: Compass },
    { label: "compare", Icon: ArrowsLeftRight },
    { label: "reports", Icon: FileText },
    { label: "studio", Icon: Sparkle },
    { label: "settings", Icon: GearSix },
  ];
  const isActivityReport = /^\/app\/activities\/[^/]+$/u.test(path);
  return (
    <div className={`app-shell${isActivityReport ? " activity-focus-shell" : ""}`}>
      <aside className="side-nav">
        <Link href="/" className="brand">
          <Mark />
          <span>SportTrack</span>
        </Link>
        <nav aria-label={messages[locale].appNavigation}>
          {navItems.map(({ label, Icon }, index) => (
            <Link
              key={label}
              href={`/app${label === "home" ? "" : `/${label}`}`}
              className={section === label ? "active" : ""}
            >
              <Icon aria-hidden="true" size={19} weight={section === label ? "fill" : "regular"} />
              {t.nav[index]}
            </Link>
          ))}
        </nav>
        <div className="privacy-chip">
          <span>●</span>
          <div>
            <strong>{t.private}</strong>
            <small>{t.privacyChip}</small>
          </div>
        </div>
      </aside>
      <header className="app-top">
        <Link href="/" className="brand compact">
          <Mark />
          <span>SportTrack</span>
        </Link>
        <div>
          <LanguageSelect
            locale={locale}
            onChange={(next) => {
              const current = accountPreferences;
              if (current === null) {
                toggle(next);
                return;
              }
              void fetch("/v1/preferences", {
                method: "PUT",
                credentials: "include",
                headers: { "content-type": "application/json", "if-match": `"${current.version}"` },
                body: JSON.stringify({ ...current, locale: next, version: undefined }),
              }).then(async (response) => {
                if (!response.ok) return;
                const payload = (await response.json()) as Envelope<
                  Preferences & { version: number }
                >;
                setAccountPreferences(payload.data);
                toggle(next);
              });
            }}
          />
          <span className="avatar" aria-label={t.avatarLabel}>
            ST
          </span>
        </div>
      </header>
      <main id="main" className={`app-main${isActivityReport ? " activity-main" : ""}`}>
        <PrivateWorkspace
          locale={locale}
          section={section}
          path={path}
          studio={<Studio locale={locale} />}
        />
      </main>
      <nav className="bottom-nav" aria-label={messages[locale].mobileNavigation}>
        {navItems.slice(0, 5).map(({ label, Icon }, index) => (
          <Link
            key={label}
            href={`/app${label === "home" ? "" : `/${label}`}`}
            className={section === label ? "active" : ""}
          >
            <Icon aria-hidden="true" size={21} weight={section === label ? "fill" : "regular"} />
            <small>{t.nav[index]}</small>
          </Link>
        ))}
      </nav>
    </div>
  );
}

function AuthGate({
  locale,
  toggle,
  onSignedIn,
}: {
  locale: Locale;
  toggle: (next: Locale) => void;
  onSignedIn: () => void;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState("");
  async function signIn(event: FormEvent) {
    event.preventDefault();
    setStatus(locale !== "zh-CN" ? "Signing in…" : "正在登录…");
    const response = await fetch("/api/auth/sign-in/email", {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ email, password, callbackURL: "/app" }),
    });
    if (response.ok) onSignedIn();
    else
      setStatus(
        locale !== "zh-CN"
          ? "Sign-in was not completed. Verify your email or use recovery."
          : "登录未完成，请验证邮箱或使用恢复流程。",
      );
  }
  async function recover() {
    setStatus(locale !== "zh-CN" ? "Requesting recovery…" : "正在请求恢复…");
    await fetch("/api/auth/request-password-reset", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ email, redirectTo: "/app" }),
    });
    setStatus(
      locale !== "zh-CN"
        ? "If the account exists, a short-lived recovery message has been sent."
        : "如果账户存在，系统已发送短时有效的恢复邮件。",
    );
  }
  return (
    <div className="auth-page">
      <PublicHeader locale={locale} toggle={toggle} />
      <main id="main" className="auth-card">
        <p className="eyebrow">{copy[locale].signInEyebrow}</p>
        <h1>{locale !== "zh-CN" ? "Return to your evidence" : "返回你的证据"}</h1>
        <p>
          {locale !== "zh-CN"
            ? "Credentials stay in secure cookies and are never stored in browser application storage."
            : "凭据保存在安全 Cookie 中，不会写入浏览器应用存储。"}
        </p>
        <form onSubmit={(event) => void signIn(event)}>
          <label>
            <span>{locale !== "zh-CN" ? "Verified email" : "已验证邮箱"}</span>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
          <label>
            <span>{locale !== "zh-CN" ? "Password" : "密码"}</span>
            <input
              type="password"
              autoComplete="current-password"
              minLength={12}
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </label>
          <button className="button">{copy[locale].signIn}</button>
          <button className="text-button" type="button" onClick={() => void recover()}>
            {locale !== "zh-CN" ? "Start account recovery" : "开始账户恢复"}
          </button>
        </form>
        <p role="status">{status}</p>
        <p>
          <a href="/#eligibility">
            {locale !== "zh-CN"
              ? "New here? Confirm availability and create an account."
              : "首次使用？请确认服务可用并创建账户。"}
          </a>
        </p>
      </main>
    </div>
  );
}

function Join({ locale, toggle }: { locale: Locale; toggle: (next: Locale) => void }) {
  const evidenceId = sessionStorage.getItem("sporttrack-evidence");
  const [status, setStatus] = useState("");
  async function enroll(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (evidenceId === null) {
      setStatus(locale !== "zh-CN" ? "Complete eligibility first." : "请先完成资格检查。");
      return;
    }
    const values = new FormData(event.currentTarget);
    const response = await fetch("/v1/enrollment", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        evidenceId,
        name: values.get("name"),
        email: values.get("email"),
        password: values.get("password"),
        locale,
      }),
    });
    if (response.ok) {
      sessionStorage.removeItem("sporttrack-evidence");
      setStatus(
        locale !== "zh-CN"
          ? "Account created. Check your email to verify it, then sign in."
          : "账户已创建，请检查邮箱完成验证后登录。",
      );
    } else
      setStatus(
        locale !== "zh-CN"
          ? "Account creation was not completed. Check the details or use recovery if the address is already registered."
          : "账户创建未完成，请检查信息；如果邮箱已注册，请使用恢复流程。",
      );
  }
  return (
    <div className="auth-page">
      <PublicHeader locale={locale} toggle={toggle} />
      <main id="main" className="auth-card">
        <p className="eyebrow">{copy[locale].joinEyebrow}</p>
        <h1>{locale !== "zh-CN" ? "Create your private account" : "创建你的私密账户"}</h1>
        <p>
          {locale !== "zh-CN"
            ? "No route or sensor file is collected during enrollment."
            : "注册期间不会收集路线或传感器文件。"}
        </p>
        {evidenceId === null ? (
          <p className="notice">
            {locale !== "zh-CN"
              ? "Eligibility evidence is missing or expired."
              : "资格证明缺失或已过期。"}{" "}
            <Link href="/#eligibility">{locale !== "zh-CN" ? "Check again" : "重新检查"}</Link>
          </p>
        ) : (
          <form onSubmit={(event) => void enroll(event)}>
            <label>
              <span>{locale !== "zh-CN" ? "Display name" : "显示名称"}</span>
              <input name="name" required maxLength={100} />
            </label>
            <label>
              <span>{locale !== "zh-CN" ? "Email" : "邮箱"}</span>
              <input name="email" type="email" autoComplete="email" required />
            </label>
            <label>
              <span>
                {locale !== "zh-CN" ? "Password (12+ characters)" : "密码（至少 12 个字符）"}
              </span>
              <input
                name="password"
                type="password"
                autoComplete="new-password"
                minLength={12}
                maxLength={128}
                required
              />
            </label>
            <button className="button">
              {locale !== "zh-CN" ? "Create and verify" : "创建并验证"}
            </button>
          </form>
        )}
        <p role="status">{status}</p>
      </main>
    </div>
  );
}

function Studio({ locale }: { locale: Locale }) {
  const t = copy[locale];
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const renderControllerRef = useRef<AbortController | null>(null);
  const [status, setStatus] = useState("");
  const [label, setLabel] = useState("");
  const [snapshot, setSnapshot] = useState<null | {
    id: string;
    policyVersion: string;
    artifact: {
      route: { latitude: number; longitude: number }[][] | null;
      includedMetricKeys: string[];
    };
  }>(null);
  useEffect(() => {
    const controller = new AbortController();
    fetch("/v1/redaction-snapshots", { signal: controller.signal })
      .then((response) => response.json())
      .then(async (payload: Envelope<{ snapshots: Array<{ id: string }> }>) => {
        const id = payload.data.snapshots[0]?.id;
        if (id === undefined) {
          setStatus(
            locale !== "zh-CN"
              ? "Create and review a Redaction Snapshot before using Studio."
              : "使用工作室前，请先创建并审核脱敏快照。",
          );
          return;
        }
        const response = await fetch(`/v1/redaction-snapshots/${id}`, {
          signal: controller.signal,
        });
        const detail = (await response.json()) as Envelope<typeof snapshot>;
        if (detail.data !== null) {
          setSnapshot(detail.data);
          setLabel(locale !== "zh-CN" ? "PRIVATE ACTIVITY" : "私密活动");
        }
      })
      .catch(() =>
        setStatus(
          locale !== "zh-CN"
            ? "Studio could not load the authorized snapshot."
            : "工作室无法加载已授权的快照。",
        ),
      );
    return () => controller.abort();
  }, [locale]);
  const draw = useCallback(
    async (download: boolean) => {
      renderControllerRef.current?.abort();
      const renderController = new AbortController();
      renderControllerRef.current = renderController;
      const { signal } = renderController;
      const canvas = canvasRef.current;
      if (canvas === null) return;
      if (snapshot === null) {
        setStatus(
          locale !== "zh-CN"
            ? "Create and review a Redaction Snapshot first."
            : "请先创建并审核脱敏快照。",
        );
        return;
      }
      canvas.width = 1080;
      canvas.height = 1350;
      const context = canvas.getContext("2d");
      if (context === null) {
        setStatus(locale !== "zh-CN" ? "Canvas is unavailable." : "画布不可用。");
        return;
      }
      context.fillStyle = "#0b1713";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.strokeStyle = "#bdea4b";
      context.lineWidth = 14;
      context.lineCap = "round";
      const route = snapshot.artifact.route ?? [];
      const pointCount = route.reduce((total, segment) => total + segment.length, 0);
      if (pointCount > 1) {
        let minLat = Number.POSITIVE_INFINITY;
        let maxLat = Number.NEGATIVE_INFINITY;
        let minLon = Number.POSITIVE_INFINITY;
        let maxLon = Number.NEGATIVE_INFINITY;
        function* routePoints() {
          for (const segment of route) yield* segment;
        }
        await cooperativelyVisit(routePoints(), signal, ({ latitude, longitude }) => {
          minLat = Math.min(minLat, latitude);
          maxLat = Math.max(maxLat, latitude);
          minLon = Math.min(minLon, longitude);
          maxLon = Math.max(maxLon, longitude);
        });
        for (const segment of route) {
          context.beginPath();
          await cooperativelyVisit(segment, signal, ({ latitude, longitude }, index) => {
            const x =
              120 + ((longitude - minLon) / Math.max(maxLon - minLon, Number.EPSILON)) * 840;
            const y = 880 - ((latitude - minLat) / Math.max(maxLat - minLat, Number.EPSILON)) * 560;
            if (index === 0) context.moveTo(x, y);
            else context.lineTo(x, y);
          });
          context.stroke();
        }
      } else {
        context.strokeStyle = "#365247";
        context.strokeRect(100, 320, 880, 560);
        context.fillStyle = "#9eb5aa";
        context.font = "600 44px system-ui";
        context.fillText(
          locale !== "zh-CN" ? "MAPLESS FIXED SNAPSHOT" : "无地图固定快照",
          210,
          620,
        );
      }
      context.fillStyle = "#f3f4e8";
      context.font = "700 76px system-ui";
      context.fillText(
        (label || (locale !== "zh-CN" ? "PRIVATE ACTIVITY" : "私密活动")).slice(0, 24),
        100,
        150,
      );
      context.font = "500 30px system-ui";
      context.fillStyle = "#9eb5aa";
      context.fillText(
        `${snapshot.policyVersion.toUpperCase()} · ${snapshot.artifact.includedMetricKeys.join(" · ").toUpperCase()}`.slice(
          0,
          58,
        ),
        100,
        215,
      );
      context.font = "500 30px system-ui";
      context.fillStyle = "#bdea4b";
      context.fillText(`SPORTTRACK / ${snapshot.id.slice(0, 8).toUpperCase()}`, 100, 1240);
      if (!download || signal.aborted) return;
      canvas.toBlob((blob) => {
        if (signal.aborted) return;
        if (blob === null) {
          setStatus(locale !== "zh-CN" ? "PNG creation failed." : "PNG 创建失败。");
          return;
        }
        const link = document.createElement("a");
        link.download = `sporttrack-${snapshot.id.slice(0, 8)}.png`;
        link.href = URL.createObjectURL(blob);
        link.click();
        setTimeout(() => URL.revokeObjectURL(link.href), 1000);
        setStatus(
          locale !== "zh-CN"
            ? "PNG created locally from the fixed snapshot."
            : "PNG 已根据固定快照在本地创建。",
        );
      }, "image/png");
    },
    [label, locale, snapshot],
  );
  useEffect(() => {
    if (snapshot !== null)
      void draw(false).catch((error: unknown) => {
        if (!(error instanceof DOMException) || error.name !== "AbortError") throw error;
      });
    return () => renderControllerRef.current?.abort();
  }, [draw, snapshot]);
  return (
    <>
      <header className="app-title">
        <div>
          <p className="eyebrow">{t.studioEyebrow}</p>
          <h1>{t.studio}</h1>
          <p>{t.maplessFirst}</p>
        </div>
      </header>
      <section className="studio-grid">
        <canvas
          ref={canvasRef}
          width="540"
          height="675"
          aria-label={locale !== "zh-CN" ? "Privacy-safe studio preview" : "隐私安全工作室预览"}
        />
        <div className="studio-controls">
          <p className="section-index">{t.studioTemplate}</p>
          <h2>{locale !== "zh-CN" ? "Reviewed snapshot composition" : "已审核快照构图"}</h2>
          <p>{t.pngHelp}</p>
          <label>
            <span>{locale !== "zh-CN" ? "Athlete-controlled label" : "由运动者控制的标签"}</span>
            <input
              value={label}
              maxLength={24}
              onChange={(event) => setLabel(event.target.value)}
            />
          </label>
          <button
            className="button"
            disabled={snapshot === null}
            onClick={() => {
              void draw(true).catch((error: unknown) => {
                if (!(error instanceof DOMException) || error.name !== "AbortError") throw error;
              });
            }}
          >
            {t.png}
          </button>
          <p role="status">{status}</p>
        </div>
      </section>
    </>
  );
}

export function App() {
  const [locale, toggle] = useLocale();
  const [path, setPath] = useState(location.pathname);
  useEffect(() => {
    const update = () => setPath(location.pathname);
    addEventListener("popstate", update);
    return () => removeEventListener("popstate", update);
  }, []);
  const section = useMemo(() => path.split("/")[2] || "home", [path]);
  if (path === "/demo") return <Demo locale={locale} toggle={toggle} />;
  if (path === "/join") return <Join locale={locale} toggle={toggle} />;
  if (path.startsWith("/app"))
    return <AppShell locale={locale} toggle={toggle} section={section} path={path} />;
  return <Landing locale={locale} toggle={toggle} />;
}
